import { useEffect, useMemo, useState } from "react";
import { api, API, fmtErr } from "../../lib/api";
import { MagnifyingGlass, DownloadSimple, Copy, Check } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { toast } from "sonner";

export default function SymbologyLibrary({ compact = false }) {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [copied, setCopied] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/symbology/catalog", { params: { size: compact ? 110 : 150, dark: true } })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  }, [compact]);

  const cats = useMemo(
    () => [{ id: "", name: "All equipment types", count: data?.grand_total || data?.total || 0 },
      ...(data?.categories || [])],
    [data]);

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (data?.symbols || []).filter((s) =>
      (!cat || s.category === cat) &&
      (!n || `${s.name} ${s.keywords} ${s.standard} ${s.id}`.toLowerCase().includes(n)));
  }, [data, cat, q]);

  const toggle = (id) => setSel((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));

  const copySvg = async (s) => {
    try {
      await navigator.clipboard.writeText(s.svg);
      setCopied(s.id);
      setTimeout(() => setCopied(""), 1400);
    } catch {
      toast.error("Clipboard unavailable — use the SVG download instead");
    }
  };

  const legend = (ext) => {
    const ids = sel.length ? `?ids=${sel.join(",")}` : "";
    window.open(`${API}/symbology/legend.${ext}${ids}`, "_blank");
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="symbology-library">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[var(--c-border)] shrink-0">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--c-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="symbology-search"
            placeholder="Search symbol, standard or keyword (e.g. buchholz, IEC 60617, relay)"
            className="h-8 pl-8 text-xs" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="symbology-cat-select"
          className="h-8 px-2 rounded-sm border border-[var(--c-border)] bg-[var(--c-surface)] text-[11px] text-[var(--c-text)] max-w-[260px]">
          {cats.map((c) => (
            <option key={c.id || "all"} value={c.id}>{c.name}{c.count ? ` (${c.count})` : ""}</option>
          ))}
        </select>
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-muted)]"
          data-testid="symbology-count">{rows.length} / {data?.grand_total || data?.total || 0} symbols</span>
        <div className="flex items-center gap-1.5 ml-auto">
          {cat && (data?.categories || []).find((c) => c.id === cat)?.standard && (
            <span className="text-[10px] font-mono text-[var(--c-muted)] hidden lg:inline"
              data-testid="symbology-cat-standard">
              {(data.categories.find((c) => c.id === cat) || {}).standard}
            </span>
          )}
          {sel.length > 0 && (
            <span className="text-[10px] font-mono text-[var(--c-accent)]" data-testid="symbology-selected">
              {sel.length} selected
            </span>
          )}
          <button onClick={() => legend("pdf")} data-testid="symbology-legend-pdf"
            className="h-8 px-2.5 rounded-sm border border-[var(--c-border)] text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] transition-colors duration-150 flex items-center gap-1">
            <DownloadSimple size={12} /> Legend PDF
          </button>
          <button onClick={() => legend("dxf")} data-testid="symbology-legend-dxf"
            className="h-8 px-2.5 rounded-sm border border-[var(--c-border)] text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] transition-colors duration-150 flex items-center gap-1">
            <DownloadSimple size={12} /> Legend DXF
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {loading ? (
          <div className="text-xs text-[var(--c-muted)] p-4" data-testid="symbology-loading">Loading symbol library…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-[var(--c-muted)] p-4" data-testid="symbology-empty">No symbol matches “{q}”.</div>
        ) : (
          <div className={`grid gap-2.5 ${compact
            ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
            : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"}`}>
            {rows.map((s) => {
              const on = sel.includes(s.id);
              return (
                <div key={s.id} data-testid={`symbol-card-${s.id}`}
                  onClick={() => toggle(s.id)}
                  className={`group relative rounded-sm border bg-[var(--c-surface)] p-2.5 cursor-pointer transition-colors duration-150 ${on
                    ? "border-[var(--c-accent)] bg-[var(--c-accent)]/10"
                    : "border-[var(--c-border)] hover:border-[var(--c-accent)]/60"}`}>
                  <div className="h-[104px] flex items-center justify-center overflow-hidden text-[var(--c-text)]"
                    dangerouslySetInnerHTML={{ __html: s.svg }} />
                  <div className="mt-1.5 text-[11px] leading-tight text-[var(--c-text)]">{s.name}</div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <span className="text-[9px] font-mono text-[var(--c-muted)] truncate">{s.standard}</span>
                    {s.designation && (
                      <span className="ml-auto text-[9px] font-mono px-1 rounded-sm border border-[var(--c-border)] text-[var(--c-muted)]"
                        title="IEC 81346 reference designation">{s.designation}</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button onClick={(e) => { e.stopPropagation(); copySvg(s); }}
                      data-testid={`symbol-copy-${s.id}`}
                      className="h-6 px-1.5 rounded-sm border border-[var(--c-border)] text-[10px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
                      {copied === s.id ? <Check size={10} /> : <Copy size={10} />} SVG
                    </button>
                    <a href={`${API}/symbology/${s.id}.dxf`} target="_blank" rel="noreferrer"
                      onClick={(e) => e.stopPropagation()} data-testid={`symbol-dxf-${s.id}`}
                      className="h-6 px-1.5 rounded-sm border border-[var(--c-border)] text-[10px] text-[var(--c-muted)] hover:text-[var(--c-text)] flex items-center gap-1">
                      <DownloadSimple size={10} /> DXF
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
